import React, { useMemo, useState } from 'react'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { rectSwappingStrategy, SortableContext } from '@dnd-kit/sortable'

import {
  AttachmentData,
  AttachmentDataWithRequiredAttachment,
  AttachmentStatus,
  AttachmentType,
  Styles,
} from '../../../../../../../../@types/types'
import Picture from './Picture'
import { convertImageUrl } from '../../../../../../../../services/conversion/conversionServices'
import { changeImageOrder } from '../../../../../../../../redux/thunks/multimedia/multimedia'
import { useAppDispatch } from '../../../../../../../../app/hooks'
import UploadingList from '../common/UploadingList'

const style: Styles = {
  dragOverlay: { paddingLeft: '32px', cursor: 'grabbing' },
  fullwidth: { width: '100%' },
}

export interface PictureListProps {
  attachmentData: AttachmentData[]
}

export const PictureList: React.FC<PictureListProps> = ({ attachmentData }) => {
  const dispatch = useAppDispatch()

  const [activeId, setActiveId] = useState<UniqueIdentifier>()

  const handleDragEnd = (event: DragEndEvent) => {
    const { over, active } = event

    over && active.id !== over.id && dispatch(changeImageOrder(active.id, over.id))

    setActiveId(undefined)
  }

  const activeAttachment = useMemo(
    () => (activeId !== undefined ? attachmentData.find((item) => getMultimediaUniqueKey(item) === activeId) : null),
    [attachmentData, activeId]
  )

  const mouseSensor = useSensor(MouseSensor, { activationConstraint: { distance: 10 } })
  const sensors = useSensors(mouseSensor, useSensor(TouchSensor), useSensor(KeyboardSensor))
  const mappedAttachments = attachmentData.map(getMultimediaUniqueKey)
  const items = mappedAttachments as unknown as string[]

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => setActiveId(event.active.id)}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
      data-testid='picture-list'
    >
      <SortableContext items={items} strategy={rectSwappingStrategy}>
        <PersistedPictureList attachments={attachmentData} />
      </SortableContext>
      <UploadingList
        attachments={attachmentData}
        attachmentType={AttachmentType.PICTURE}
        key={'pictures-uploading-list'}
      />
      <DragOverlay>
        <AttachmentBeingDragged activeAttachment={activeAttachment} />
      </DragOverlay>
    </DndContext>
  )
}

function PersistedPictureList({ attachments }: { attachments: AttachmentData[] }) {
  return attachments
    .filter((it) => it.state === AttachmentStatus.PERSISTED && it.attachment)
    .map((data, index) =>
      isPictureType(data) ? (
        <Picture data={data as AttachmentDataWithRequiredAttachment} index={index} key={getMultimediaUniqueKey(data)} />
      ) : undefined
    )
}

export const getMultimediaUniqueKey = (item: AttachmentData) => {
  switch (item.state) {
    case AttachmentStatus.PERSISTED:
      return item.attachment!.id
    case AttachmentStatus.ERROR:
    case AttachmentStatus.UPLOADING:
      return item.file!.id
  }
}

function isPictureType(attachment: AttachmentData) {
  return attachment.attachmentType === AttachmentType.PICTURE
}

function AttachmentBeingDragged({ activeAttachment }: { activeAttachment: AttachmentData | undefined | null }) {
  const isDraggable =
    activeAttachment &&
    activeAttachment.state === AttachmentStatus.PERSISTED &&
    activeAttachment.attachment?.type === AttachmentType.PICTURE

  return (
    isDraggable && (
      <div className='grid-item' style={style.dragOverlay}>
        <img alt='bild' style={style.fullwidth} src={convertImageUrl('320', '160', activeAttachment.attachment?.url)} />
      </div>
    )
  )
}

export default PictureList
